import { useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { aiAvailabilityKey, fetchAiAvailability } from '../api/aiAvailability'
import { setPending } from '../api/assistantPending'
import { logout } from '../api/auth'
import { fetchMe, fetchTenantBrand, fetchTenantOptions, ME_QUERY_KEY, MENU_PERMISSIONS } from '../api/session'
import { getWorkTenantId, setWorkTenantId, WORK_TENANT_PERMISSION } from '../api/workTenant'
import { AppShell, type WorkArea } from './AppShell'
import { ErrorState } from './ErrorState'
import { LoadingState } from './LoadingState'
import { TopSearch } from './TopSearch'
import { VigaDock } from './VigaDock'
import { t } from '../i18n'

/**
 * B1 (sessao 35) - porta das telas internas.
 *
 * Pergunta ao servidor quem esta logado (`/api/auth/me`, via cookie HttpOnly --
 * a SPA nao guarda token, S22-5). Anonimo vai para `/login`. Logado entra na
 * casca (`AppShell`) com a marca da empresa em que esta trabalhando.
 * A marca que falha nao derruba a casca: sem ela, o cartao mostra vazio.
 *
 * s54 (item 2 fase 1): quem tem `tenant.list` (platform_admin) escolhe a EMPRESA em que trabalha. A escolha vive
 * no sessionStorage (`api/workTenant.ts`) e o `apiFetch` a manda em `X-Tenant-ID`. Trocar de empresa DESCARTA
 * tudo o que foi lido (dado de uma empresa nunca aparece na tela de outra) e volta ao inicio. Quem nao tem a
 * permissao nunca herda uma escolha antiga da aba. "Sair" apaga a escolha.
 *
 * s67 / ADR-0033 2i: aqui se le se a IA esta ligada para a empresa de trabalho (`/api/ai/availability`, sem nova tentativa;
 * falhou = desligada) e se monta, para quem pode usar o assistente, o rotulo do menu (V1), a BUSCA no topo (R2) e o
 * PAINEL da VIGA (V3). A busca deixa a pergunta pendente no cache e abre o painel; em /assistente a propria pagina a envia.
 */
const KEEP_ON_SWITCH = new Set<unknown>([ME_QUERY_KEY[0], 'tenant-options'])

export function RequireSession() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [workTenantId, setWorkTenantState] = useState<string | null>(getWorkTenantId())
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const meData = me.data
  const logged = meData?.authenticated === true
  const ownTenantId = meData?.authenticated ? meData.tenantId : null
  const canSwitch = meData?.authenticated === true && meData.permissions.includes(WORK_TENANT_PERMISSION)
  const activeWork = canSwitch && workTenantId !== null && workTenantId !== ownTenantId ? workTenantId : null
  const brandTenantId = activeWork ?? ownTenantId
  const brand = useQuery({
    queryKey: ['tenant-brand', brandTenantId],
    queryFn: () => fetchTenantBrand(brandTenantId as string),
    enabled: brandTenantId !== null,
  })
  const options = useQuery({ queryKey: ['tenant-options'], queryFn: fetchTenantOptions, enabled: canSwitch })
  const canAssist = meData?.authenticated === true && meData.permissions.includes(MENU_PERMISSIONS.jobs)
  const ia = useQuery({ queryKey: aiAvailabilityKey(), queryFn: fetchAiAvailability, retry: false, enabled: canAssist })
  const iaLigada = canAssist && ia.data === true
  const [dockOpen, setDockOpen] = useState(false)
  const closeDock = useCallback(() => setDockOpen(false), [])

  useEffect(() => {
    if (logged && !canSwitch && getWorkTenantId() !== null) {
      setWorkTenantId(null)
      setWorkTenantState(null)
    }
  }, [logged, canSwitch])

  if (me.isPending) return <LoadingState />
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />
  if (!me.data.authenticated) return <Navigate to="/login" replace />

  function changeWorkTenant(id: string | null) {
    const next = id !== null && id !== ownTenantId ? id : null
    setWorkTenantId(next)
    setWorkTenantState(next)
    queryClient.removeQueries({ predicate: (q) => !KEEP_ON_SWITCH.has(q.queryKey[0]) })
    setDockOpen(false)
    navigate('/', { replace: true })
  }

  async function signOut() {
    setWorkTenantId(null)
    setDockOpen(false)
    try {
      await logout()
    } catch {
      // Servidor fora ou sessao ja vencida: a saida local continua (o cookie
      // HttpOnly vence sozinho). Sem este catch o erro escapava sem tratamento.
    } finally {
      queryClient.clear()
      navigate('/login', { replace: true })
    }
  }

  const workArea: WorkArea | undefined =
    canSwitch && ownTenantId
      ? {
          options: options.data ?? [],
          ownTenantId,
          currentTenantId: activeWork ?? ownTenantId,
          currentName:
            options.data?.find((o) => o.id === activeWork)?.displayName ?? brand.data?.displayName ?? '',
          onChange: changeWorkTenant,
        }
      : undefined

  // s67 / 2i (R2 + J2): a busca entrega a pergunta a VIGA -- fica pendente no cache e o painel abre
  const askViga = (texto: string) => {
    setPending(queryClient, texto)
    if (pathname !== '/assistente') setDockOpen(true)
  }

  return (
    <AppShell
      me={me.data}
      tenant={brand.data ?? null}
      workArea={workArea}
      onSignOut={() => void signOut()}
      assistantLabel={iaLigada ? t.shell.assistantAi : undefined}
      search={<TopSearch permissions={me.data.permissions} iaLigada={iaLigada} onAskViga={askViga} />}
      aside={
        canAssist ? (
          <VigaDock open={dockOpen} iaLigada={iaLigada} onOpen={() => setDockOpen(true)} onClose={closeDock} />
        ) : undefined
      }
    >
      <Outlet />
    </AppShell>
  )
}
